import { useEffect, useRef, useState } from "react";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Printer, X, Download, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { PrintShopPdfButton } from "@/components/PrintShopPdfButton";
import { elementToPdfBytes, elementToPngBlob } from "@/lib/printExport";
import { ToolInfoSection } from "@/components/ToolInfoSection";

interface MenuItem {
  id: number;
  name: string;
  description: string;
  price: string;
  imageUrl?: string;
}
interface Section {
  id: number;
  title: string;
  items: MenuItem[];
  // Marks this section as starting a fresh printed/PDF page -- only
  // meaningful for Print/Save as PDF, since a single PNG export from
  // Download as Image has no concept of "pages" at all (it's one
  // continuous image either way).
  newPage?: boolean;
}

// Genuinely distinct font pairings per theme, not just different colors on
// the same one or two fonts -- this was flagged directly ("themes mostly
// look similar"), so each one now has its own real typographic character.
const THEMES = {
  Classic: { bg: "#FFFDF7", accent: "#8B5A2B", text: "#1F2937", font: "'Playfair Display', 'Georgia', serif", bodyFont: "'Georgia', serif" },
  Modern: { bg: "#FFFFFF", accent: "#1F2937", text: "#1F2937", font: "'Helvetica Neue', Arial, sans-serif", bodyFont: "'Helvetica Neue', Arial, sans-serif" },
  Elegant: { bg: "#FAF6F0", accent: "#7C2D12", text: "#1F2937", font: "'Cormorant Garamond', 'Palatino', serif", bodyFont: "'Palatino', serif" },
  Rustic: { bg: "#F5EDE0", accent: "#5C4A32", text: "#3A2F20", font: "'Courier New', monospace", bodyFont: "'Georgia', serif" },
  Bistro: { bg: "#1C1C1C", accent: "#D4AF37", text: "#F1F1F1", font: "'Bodoni Moda', 'Didot', serif", bodyFont: "'Palatino', serif" },
  Fresh: { bg: "#F0F7F0", accent: "#2D6A4F", text: "#1F2937", font: "'Poppins', 'Helvetica Neue', sans-serif", bodyFont: "'Helvetica Neue', sans-serif" },
};

let idCounter = 100;
const nextId = () => idCounter++;

const TOOL_INFO_CONTENT = {
  what: `Designs a restaurant or event menu with sections, items, descriptions, and pricing, using a choice of style themes, ready to print or save as a PDF. Supports multiple pages and a two-column item layout for longer menus.`,
  how: `Add sections and items with descriptions and prices, choose a theme and item layout (single or two columns), and use your browser's print function to save the finished menu as a PDF. Check "Start a new printed page here" on any section (after the first) to force a page break for Print/Save as PDF.`,
  tip: `Keep item descriptions to one short line each — a menu with long paragraph descriptions for every item becomes harder to scan quickly, which works against the actual purpose of a menu. Page breaks only apply to Print/Save as PDF — Download as Image produces one continuous image, since a single PNG has no real concept of separate pages.`,
};

export default function MenuCardMaker() {
  const { logUsageNow } = useLogUsageOnAction();
  // Several themes use real Google Fonts beyond the site-wide Inter/JetBrains
  // Mono (which is all that's loaded by default) -- without loading these
  // explicitly, they'd silently fall back to a generic serif/sans-serif and
  // the themes would keep looking nearly identical, the exact problem this
  // is meant to fix.
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Cormorant+Garamond:wght@500;600&family=Bodoni+Moda:wght@600;700&family=Poppins:wght@400;500;600&display=swap";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const [restaurantName, setRestaurantName] = useState("The Garden Table");
  const [tagline, setTagline] = useState("Farm to table, since 2020");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [theme, setTheme] = useState<keyof typeof THEMES>("Classic");
  const [customTextColor, setCustomTextColor] = useState<string | null>(null);
  const [customBgColor, setCustomBgColor] = useState<string | null>(null);
  const [customAccentColor, setCustomAccentColor] = useState<string | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [columns, setColumns] = useState<1 | 2>(1);
  const printAreaRef = useRef<HTMLDivElement>(null);

  const handleImageUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBackgroundImage(reader.result as string);
    reader.readAsDataURL(file);
  };
  const handleLogoUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoImage(reader.result as string);
    reader.readAsDataURL(file);
  };
  const handleItemImageUpload = (sectionId: number, itemId: number, files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSections(prev =>
        prev.map(s =>
          s.id === sectionId
            ? { ...s, items: s.items.map(i => (i.id === itemId ? { ...i, imageUrl: reader.result as string } : i)) }
            : s
        )
      );
    };
    reader.readAsDataURL(file);
  };

  const downloadAsImage = async () => {
    if (!printAreaRef.current) return;
    try {
      const blob = await elementToPngBlob(printAreaRef.current); // ~300 DPI at 8.5 in wide
      downloadBlob(blob, `${restaurantName.replace(/\s+/g, "-").toLowerCase() || "menu"}.png`);
      toast.success("Menu downloaded as image!");
    } catch (err) {
      console.error("Failed to export menu as image:", err);
      toast.error("Couldn't export the image. Try the Print / Save as PDF option instead.");
    }
  };

  const downloadAsPdf = async () => {
    if (!printAreaRef.current) return;
    try {
      const bytes = await elementToPdfBytes(printAreaRef.current);
      downloadBlob(new Blob([bytes as BlobPart], { type: "application/pdf" }), "menu.pdf");
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF export failed:", err);
      toast.error("Couldn't build the PDF. Try the image download instead.");
    }
  };
  const [sections, setSections] = useState<Section[]>([
    {
      id: nextId(),
      title: "Appetizers",
      items: [
        {
          id: nextId(),
          name: "Bruschetta",
          description: "Tomato, basil, garlic on toasted baguette",
          price: "9",
        },
        {
          id: nextId(),
          name: "Soup of the Day",
          description: "Ask your server",
          price: "7",
        },
      ],
    },
    {
      id: nextId(),
      title: "Main Courses",
      items: [
        {
          id: nextId(),
          name: "Grilled Salmon",
          description: "Lemon butter, seasonal vegetables",
          price: "24",
        },
        {
          id: nextId(),
          name: "Mushroom Risotto",
          description: "Wild mushroom, parmesan, truffle oil",
          price: "19",
        },
      ],
    },
  ]);

  const addSection = () =>
    setSections([
      ...sections,
      { id: nextId(), title: "New Section", items: [] },
    ]);
  const removeSection = (id: number) =>
    setSections(sections.filter(s => s.id !== id));
  const updateSectionTitle = (id: number, title: string) =>
    setSections(sections.map(s => (s.id === id ? { ...s, title } : s)));
  const toggleSectionPageBreak = (id: number) =>
    setSections(sections.map(s => (s.id === id ? { ...s, newPage: !s.newPage } : s)));

  const addItem = (sectionId: number) =>
    setSections(
      sections.map(s =>
        s.id === sectionId
          ? {
              ...s,
              items: [
                ...s.items,
                { id: nextId(), name: "New Item", description: "", price: "0" },
              ],
            }
          : s
      )
    );
  const removeItem = (sectionId: number, itemId: number) =>
    setSections(
      sections.map(s =>
        s.id === sectionId
          ? { ...s, items: s.items.filter(i => i.id !== itemId) }
          : s
      )
    );
  const updateItem = (
    sectionId: number,
    itemId: number,
    field: keyof MenuItem,
    value: string
  ) =>
    setSections(
      sections.map(s =>
        s.id === sectionId
          ? {
              ...s,
              items: s.items.map(i =>
                i.id === itemId ? { ...i, [field]: value } : i
              ),
            }
          : s
      )
    );

  const t = THEMES[theme];

  return (
    <ToolContainer>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #menu-print-area, #menu-print-area * { visibility: visible; }
          #menu-print-area {
            position: absolute; top: 0; left: 0; width: 100%;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          #menu-print-area .menu-section-page-break {
            page-break-before: always;
          }
          @page { margin: 0.5in; }
        }
      `}</style>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 print:block">
        <div className="space-y-4 print:hidden">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block text-sm">
                Restaurant / Event Name
              </Label>
              <Input
                value={restaurantName}
                onChange={e => setRestaurantName(e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Tagline</Label>
              <Input aria-label="Tagline"
                value={tagline}
                onChange={e => setTagline(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="mb-1 block text-sm">Address (optional)</Label>
              <Input aria-label="Address (optional)" value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St" />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Phone (optional)</Label>
              <Input aria-label="Phone (optional)" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Email (optional)</Label>
              <Input aria-label="Email (optional)" value={email} onChange={e => setEmail(e.target.value)} placeholder="hello@restaurant.com" />
            </div>
          </div>
          <div>
            <Label className="mb-1 block text-sm">Logo (optional)</Label>
            {logoImage ? (
              <div className="flex items-center gap-2">
                <img src={logoImage} className="h-10 w-10 object-contain rounded border bg-white" />
                <Button size="sm" variant="ghost" onClick={() => setLogoImage(null)}>
                  <X className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <FileDropZone gateAtDownload accept="image/*" onFiles={handleLogoUpload} label="Drop a restaurant logo" />
            )}
          </div>
          <div>
            <Label className="mb-1 block text-sm">Theme</Label>
            <div className="flex flex-wrap gap-2">
              {Object.keys(THEMES).map(th => (
                <Button
                  key={th}
                  size="sm"
                  variant={theme === th ? "default" : "outline"}
                  onClick={() => setTheme(th as keyof typeof THEMES)}
                >
                  {th}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-1 block text-sm">Item Layout</Label>
            <div className="flex gap-2">
              <Button size="sm" variant={columns === 1 ? "default" : "outline"} onClick={() => setColumns(1)}>
                Single Column
              </Button>
              <Button size="sm" variant={columns === 2 ? "default" : "outline"} onClick={() => setColumns(2)}>
                Two Columns
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 items-end">
            <div>
              <Label className="mb-1 block text-sm">Background Color</Label>
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={customBgColor ?? t.bg}
                  onChange={e => setCustomBgColor(e.target.value)}
                  className="h-9 w-9 rounded border cursor-pointer"
                />
                {customBgColor && (
                  <Button size="sm" variant="ghost" className="px-2" onClick={() => setCustomBgColor(null)}>Reset</Button>
                )}
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-sm">Accent Color</Label>
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={customAccentColor ?? t.accent}
                  onChange={e => setCustomAccentColor(e.target.value)}
                  className="h-9 w-9 rounded border cursor-pointer"
                />
                {customAccentColor && (
                  <Button size="sm" variant="ghost" className="px-2" onClick={() => setCustomAccentColor(null)}>Reset</Button>
                )}
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-sm">Text Color</Label>
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={customTextColor ?? t.text}
                  onChange={e => setCustomTextColor(e.target.value)}
                  className="h-9 w-9 rounded border cursor-pointer"
                />
                {customTextColor && (
                  <Button size="sm" variant="ghost" className="px-2" onClick={() => setCustomTextColor(null)}>Reset</Button>
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            Colors apply live as you pick them — no separate apply button needed. "Reset" returns that color to the current theme's default.
          </p>
          <div>
            <Label className="mb-1 block text-sm">Background Photo</Label>
            {backgroundImage ? (
              <Button size="sm" variant="outline" onClick={() => setBackgroundImage(null)} className="gap-1.5">
                <X className="h-3.5 w-3.5" /> Remove Photo
              </Button>
            ) : (
              <FileDropZone gateAtDownload accept="image/*" onFiles={handleImageUpload} label="Drop a background photo" />
            )}
          </div>

          {sections.map((section, idx) => (
            <div key={section.id} className="rounded-lg border p-4">
              <div className="flex items-center gap-2 mb-3">
                <Input
                  value={section.title}
                  onChange={e => updateSectionTitle(section.id, e.target.value)}
                  className="font-semibold"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removeSection(section.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {idx > 0 && (
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!section.newPage}
                    onChange={() => toggleSectionPageBreak(section.id)}
                  />
                  Start a new printed page here (Print / Save as PDF only)
                </label>
              )}
              <div className="space-y-2">
                {section.items.map(item => (
                  <div
                    key={item.id}
                    className="grid grid-cols-[40px_1fr_70px_32px] gap-2 items-start"
                  >
                    <label className="inline-flex cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={e => {
                          const files = e.target.files ? Array.from(e.target.files) : [];
                          if (files[0]) handleItemImageUpload(section.id, item.id, files);
                          e.target.value = "";
                        }}
                      />
                      {item.imageUrl ? (
                        <img src={item.imageUrl} className="h-9 w-9 rounded object-cover border" />
                      ) : (
                        <span className="h-9 w-9 rounded border border-dashed flex items-center justify-center text-muted-foreground">
                          <ImageIcon className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </label>
                    <div className="space-y-1">
                      <Input
                        value={item.name}
                        onChange={e =>
                          updateItem(
                            section.id,
                            item.id,
                            "name",
                            e.target.value
                          )
                        }
                        placeholder="Item name"
                      />
                      <Textarea
                        value={item.description}
                        onChange={e =>
                          updateItem(
                            section.id,
                            item.id,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Description"
                        className="min-h-[40px] text-sm"
                      />
                    </div>
                    <Input
                      value={item.price}
                      onChange={e =>
                        updateItem(section.id, item.id, "price", e.target.value)
                      }
                      placeholder="$"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeItem(section.id, item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => addItem(section.id)}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={addSection}>
            <Plus className="h-4 w-4 mr-2" /> Add Section
          </Button>
          <Button className="w-full" onClick={async () => { if (await logUsageNow("download")) window.print(); }}>
            <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
          </Button>
          <Button className="w-full" variant="outline" onClick={downloadAsImage}>
            <Download className="h-4 w-4 mr-2" /> Download as Image
          </Button>
          <Button className="w-full" variant="outline" onClick={downloadAsPdf}>Download as PDF (Letter)</Button>
          <PrintShopPdfButton target={printAreaRef} filename="menu-print-shop.pdf" defaultWidthIn={5.5} />
        </div>

        <div
          id="menu-print-area"
          ref={printAreaRef}
          className="rounded-lg border p-10 print:border-0 print:rounded-none relative overflow-hidden self-start"
          style={{ backgroundColor: customBgColor ?? t.bg, fontFamily: t.font }}
        >
          {backgroundImage && (
            <>
              <img src={backgroundImage} className="absolute inset-0 w-full h-full object-cover" />
              {/* A photo's own colors can't be predicted, so a semi-opaque
                  overlay in the theme's own background color keeps the
                  menu text readable regardless of what's in the photo. */}
              <div className="absolute inset-0" style={{ backgroundColor: customBgColor ?? t.bg, opacity: 0.82 }} />
            </>
          )}
          <div className="relative">
          <div className="text-center mb-8">
            {logoImage && (
              <img src={logoImage} alt={`${restaurantName} logo`} className="h-16 w-16 object-contain mx-auto mb-3" />
            )}
            <div className="text-3xl font-bold" style={{ color: customAccentColor ?? t.accent }}>
              {restaurantName}
            </div>
            <p className="text-sm mt-1 opacity-70" style={{ color: customTextColor ?? t.text, fontFamily: t.bodyFont }}>{tagline}</p>
            {(address || phone || email) && (
              <p className="text-xs mt-2 opacity-60" style={{ color: customTextColor ?? t.text, fontFamily: t.bodyFont }}>
                {[address, phone, email].filter(Boolean).join("  •  ")}
              </p>
            )}
          </div>
          {sections.map((section, idx) => (
            <div key={section.id}>
              {idx > 0 && section.newPage && (
                <div className="print:hidden flex items-center gap-3 my-6 text-xs text-muted-foreground">
                  <div className="flex-1 border-t border-dashed" />
                  New printed page starts here
                  <div className="flex-1 border-t border-dashed" />
                </div>
              )}
              <div className={`mb-6 ${idx > 0 && section.newPage ? "menu-section-page-break" : ""}`}>
              <h2
                className="text-lg font-semibold mb-3 pb-1 border-b"
                style={{ color: customAccentColor ?? t.accent, borderColor: customAccentColor ?? t.accent }}
              >
                {section.title}
              </h2>
              <div className={columns === 2 ? "grid grid-cols-2 gap-x-6 gap-y-3" : "space-y-3"}>
                {section.items.map(item => (
                  <div key={item.id} className="flex justify-between gap-4">
                    {item.imageUrl && (
                      <img src={item.imageUrl} className="w-14 h-14 rounded object-cover shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium" style={{ color: customTextColor ?? t.text, fontFamily: t.bodyFont }}>{item.name}</p>
                      {item.description && (
                        <p className="text-sm opacity-70" style={{ color: customTextColor ?? t.text, fontFamily: t.bodyFont }}>{item.description}</p>
                      )}
                    </div>
                    <p
                      className="font-medium shrink-0"
                      style={{ color: customAccentColor ?? t.accent }}
                    >
                      ${item.price}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            </div>
          ))}
          </div>
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
